import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';

import { api } from '../services/api';
import { fieldSectionSx } from './deviceConfigStyles';

// =============================================================================
// PortForwardingSection
//
// Renders the port-forwarding card that lives inside the device config
// "Fields" view. Visible only for jnet2 network boards (Nodes and CAPs).
// Lets the operator add / edit / delete rules and Save them all in one
// PUT — the backend preserves any non-port-forward `app[]` entries
// (e.g. {"fw":"dis"}) so this section never clobbers other firewall
// state on the device.
// =============================================================================

const PROTOCOLS = ['tcp', 'udp', 'tcpudp'];
const INTERFACES = ['wan', 'lan'];

const formatProtoLabel = (proto) => {
  const p = String(proto || '').toLowerCase();
  if (p === 'tcpudp') return 'TCP & UDP';
  return p.toUpperCase();
};

const ON_OFF_OPTIONS = [
  { value: '0', label: 'Off' },
  { value: '1', label: 'On' },
];

const NAME_PATTERN = /^[A-Za-z0-9_.-]+$/;

const blankRule = (serial) => ({
  // Auto-prefix the rule name with the device serial so they are unique
  // across the fleet — matches the convention shown in the PRD example.
  name: serial ? `${serial}_` : '',
  proto: 'tcp',
  src: 'wan',
  src_dport: '',
  dst: 'lan',
  dest_port: '',
  dest_ip: '',
  reflection: '1',
});

const isValidIPv4 = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return true;
  const parts = s.split('.');
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    const n = parseInt(part, 10);
    return n >= 0 && n <= 255;
  });
};

const isValidPort = (raw) => {
  const s = String(raw || '').trim();
  if (!/^\d+$/.test(s)) return false;
  const n = parseInt(s, 10);
  return n >= 1 && n <= 65535;
};

const validateRule = (rule, others) => {
  const errors = {};
  const name = (rule.name || '').trim();
  if (!name) errors.name = 'Name is required';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Allowed chars: letters, digits, _ . -';
  else if (others.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
    errors.name = 'A rule with this name already exists';
  }

  if (!PROTOCOLS.includes((rule.proto || '').toLowerCase())) errors.proto = 'Pick a protocol';
  if (!INTERFACES.includes((rule.src || '').toLowerCase())) errors.src = 'Pick an interface';
  if (!INTERFACES.includes((rule.dst || '').toLowerCase())) errors.dst = 'Pick an interface';
  if (!isValidPort(rule.src_dport)) errors.src_dport = 'Port 1-65535';
  if (!isValidPort(rule.dest_port)) errors.dest_port = 'Port 1-65535';
  const destIP = (rule.dest_ip || '').trim();
  if (destIP && !isValidIPv4(destIP)) errors.dest_ip = 'Enter a valid IPv4 address';
  if (!['0', '1'].includes(String(rule.reflection ?? '1'))) errors.reflection = 'Pick Off or On';
  return errors;
};

const ruleEqual = (a, b) => (
  a.name === b.name
  && a.proto === b.proto
  && a.src === b.src
  && a.src_dport === b.src_dport
  && a.dst === b.dst
  && a.dest_port === b.dest_port
  && (a.dest_ip || '') === (b.dest_ip || '')
  && String(a.reflection ?? '1') === String(b.reflection ?? '1')
);

const rulesArrayEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((rule, idx) => ruleEqual(rule, b[idx]));
};

const PortForwardingSection = ({ serial, isJnet2 }) => {
  const [rules, setRules] = useState([]);
  const [originalRules, setOriginalRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null); // {type, message}
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1); // -1 = new rule
  const [draft, setDraft] = useState(blankRule(serial));
  const [draftErrors, setDraftErrors] = useState({});

  const fetchRules = useCallback(async () => {
    if (!serial || !isJnet2) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getPortForwarding(serial);
      const list = Array.isArray(data?.rules) ? data.rules : [];
      setRules(list);
      setOriginalRules(list);
    } catch (err) {
      setError(err?.message || 'Failed to load port-forwarding rules');
    } finally {
      setLoading(false);
    }
  }, [serial, isJnet2]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const dirty = useMemo(() => !rulesArrayEqual(rules, originalRules), [rules, originalRules]);

  const openAdd = () => {
    setEditIndex(-1);
    setDraft(blankRule(serial));
    setDraftErrors({});
    setDialogOpen(true);
  };

  const openEdit = (idx) => {
    setEditIndex(idx);
    setDraft({
      ...blankRule(serial),
      ...rules[idx],
      dest_ip: rules[idx].dest_ip || '',
      reflection: rules[idx].reflection ?? '1',
    });
    setDraftErrors({});
    setDialogOpen(true);
  };

  const handleDelete = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDraftErrors({});
  };

  const handleDraftChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    if (draftErrors[field]) {
      setDraftErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleDialogSave = () => {
    const others = editIndex >= 0
      ? rules.filter((_, i) => i !== editIndex)
      : rules;
    const errs = validateRule(draft, others);
    if (Object.keys(errs).length > 0) {
      setDraftErrors(errs);
      return;
    }
    const cleaned = {
      name: draft.name.trim(),
      proto: draft.proto.toLowerCase(),
      src: draft.src.toLowerCase(),
      src_dport: String(parseInt(draft.src_dport, 10)),
      dst: draft.dst.toLowerCase(),
      dest_port: String(parseInt(draft.dest_port, 10)),
      reflection: String(draft.reflection ?? '1'),
    };
    const destIP = (draft.dest_ip || '').trim();
    if (destIP) {
      cleaned.dest_ip = destIP;
    }
    if (editIndex >= 0) {
      setRules((prev) => prev.map((r, i) => (i === editIndex ? cleaned : r)));
    } else {
      setRules((prev) => [...prev, cleaned]);
    }
    setDialogOpen(false);
  };

  const handleSaveAll = async () => {
    if (!serial) return;
    setSaving(true);
    setStatus(null);
    try {
      const response = await api.putPortForwarding(serial, rules);
      const newRules = Array.isArray(response?.view?.rules) ? response.view.rules : rules;
      setRules(newRules);
      setOriginalRules(newRules);
      setStatus({ type: 'success', message: 'Port-forwarding rules pushed to the device.' });
    } catch (err) {
      setStatus({ type: 'error', message: err?.message || 'Failed to update port-forwarding rules' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setRules(originalRules);
    setStatus(null);
  };

  if (!isJnet2) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={fieldSectionSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1">Port Forwarding</Typography>
          <Typography variant="body2" color="text.secondary">
            Forward inbound traffic from the WAN interface to a host on the LAN side
            (typically an AI board attached to this Node). Other firewall settings on
            the device are preserved.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh from device config">
            <span>
              <IconButton size="small" onClick={fetchRules} disabled={loading || saving}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd} disabled={loading || saving}>
            Add Rule
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchRules}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Proto</TableCell>
              <TableCell>From</TableCell>
              <TableCell align="right">External Port</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Dest IP</TableCell>
              <TableCell align="right">Internal Port</TableCell>
              <TableCell>Reflection</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    No port-forwarding rules configured.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rules.map((rule, idx) => (
              <TableRow key={`${rule.name}-${idx}`} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{rule.name}</TableCell>
                <TableCell>
                  <Chip size="small" label={formatProtoLabel(rule.proto)} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={rule.src} color={rule.src === 'wan' ? 'primary' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell align="right">{rule.src_dport}</TableCell>
                <TableCell>
                  <Chip size="small" label={rule.dst} color={rule.dst === 'lan' ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  {rule.dest_ip || '—'}
                </TableCell>
                <TableCell align="right">{rule.dest_port}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={(rule.reflection ?? '1') === '1' ? 'On' : 'Off'}
                    color={(rule.reflection ?? '1') === '1' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(idx)} disabled={saving}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(idx)} disabled={saving}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Discard Changes
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : 'Save Port-Forwarding'}
        </Button>
      </Stack>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex >= 0 ? 'Edit Port-Forward Rule' : 'New Port-Forward Rule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Rule name"
              value={draft.name}
              onChange={(e) => handleDraftChange('name', e.target.value)}
              fullWidth
              size="small"
              error={Boolean(draftErrors.name)}
              helperText={draftErrors.name || 'Unique within the device. Convention: <serial>_<purpose>, e.g. a85b36501a98_ssh'}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" fullWidth error={Boolean(draftErrors.proto)}>
                <InputLabel>Protocol</InputLabel>
                <Select
                  label="Protocol"
                  value={draft.proto}
                  onChange={(e) => handleDraftChange('proto', e.target.value)}
                >
                  {PROTOCOLS.map((p) => (
                    <MenuItem key={p} value={p}>{formatProtoLabel(p)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" fullWidth error={Boolean(draftErrors.src)}>
                <InputLabel>From (source iface)</InputLabel>
                <Select
                  label="From (source iface)"
                  value={draft.src}
                  onChange={(e) => handleDraftChange('src', e.target.value)}
                >
                  {INTERFACES.map((i) => (
                    <MenuItem key={i} value={i}>{i}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="External port (src_dport)"
                value={draft.src_dport}
                onChange={(e) => handleDraftChange('src_dport', e.target.value)}
                size="small"
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                error={Boolean(draftErrors.src_dport)}
                helperText={draftErrors.src_dport || 'Inbound port (1-65535)'}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" fullWidth error={Boolean(draftErrors.dst)}>
                <InputLabel>To (destination iface)</InputLabel>
                <Select
                  label="To (destination iface)"
                  value={draft.dst}
                  onChange={(e) => handleDraftChange('dst', e.target.value)}
                >
                  {INTERFACES.map((i) => (
                    <MenuItem key={i} value={i}>{i}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Internal port (dest_port)"
                value={draft.dest_port}
                onChange={(e) => handleDraftChange('dest_port', e.target.value)}
                size="small"
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                error={Boolean(draftErrors.dest_port)}
                helperText={draftErrors.dest_port || 'Port on the LAN host (1-65535)'}
              />
            </Stack>
            <TextField
              label="Destination IP (dest_ip)"
              value={draft.dest_ip}
              onChange={(e) => handleDraftChange('dest_ip', e.target.value)}
              size="small"
              fullWidth
              placeholder="10.50.0.201"
              error={Boolean(draftErrors.dest_ip)}
              helperText={draftErrors.dest_ip || 'Optional — LAN host to forward to (IPv4). Leave empty for device default.'}
            />
            <FormControl size="small" fullWidth error={Boolean(draftErrors.reflection)}>
              <InputLabel>Reflection</InputLabel>
              <Select
                label="Reflection"
                value={draft.reflection ?? '1'}
                onChange={(e) => handleDraftChange('reflection', e.target.value)}
              >
                {ON_OFF_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                NAT reflection (hairpin). Default is On (1).
              </Typography>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave}>
            {editIndex >= 0 ? 'Update Rule' : 'Add Rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PortForwardingSection;
